import { Upload } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import type { FormaPagamento, SolicitacaoPagamentoFormValues } from "@/services/solicitacaoPagamento";
import { PIX_TIPO_CHAVE_OPTIONS } from "./pagamentoMetodoUtils";

interface Props {
  formaPagamento: FormaPagamento | "";
  values: SolicitacaoPagamentoFormValues;
  errors: Record<string, string>;
  onPatch: (partial: Partial<SolicitacaoPagamentoFormValues>) => void;
  existingAnexoName?: string | null;
}

function FileUpload({
  anexoFile,
  existingAnexoName,
  onChange,
}: {
  anexoFile: File | null;
  existingAnexoName?: string | null;
  onChange: (file: File | null) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        Upload de arquivo
      </span>
      <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors">
        <Upload className="w-8 h-8 text-slate-400" />
        <span className="text-sm text-slate-600 text-center px-2">
          {anexoFile
            ? anexoFile.name
            : existingAnexoName
              ? `${existingAnexoName} (atual — escolha outro para substituir)`
              : "Escolher arquivo(s)"}
        </span>
        <span className="text-xs text-slate-400">PDF, imagem ou documento Office (até 15 MB)</span>
        <input
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
      </label>
    </div>
  );
}

export function PagamentoMetodoFields({
  formaPagamento,
  values,
  errors,
  onPatch,
  existingAnexoName,
}: Props) {
  if (!formaPagamento) return null;

  if (formaPagamento === "BOLETO") {
    return (
      <div className="space-y-4">
        <Input
          label="Código de barras *"
          value={values.boletoCodigoBarras}
          onChange={(e) => onPatch({ boletoCodigoBarras: e.target.value })}
          error={errors.boletoCodigoBarras}
          placeholder="Digite ou cole o código de barras"
        />
        <FileUpload
          anexoFile={values.anexoFile}
          existingAnexoName={existingAnexoName}
          onChange={(anexoFile) => onPatch({ anexoFile })}
        />
      </div>
    );
  }

  if (formaPagamento === "PIX") {
    return (
      <div className="space-y-4">
        <Select
          label="Tipo chave *"
          options={PIX_TIPO_CHAVE_OPTIONS}
          value={values.pixTipoChave}
          onChange={(e) =>
            onPatch({ pixTipoChave: e.target.value as SolicitacaoPagamentoFormValues["pixTipoChave"] })
          }
          error={errors.pixTipoChave}
          placeholder="Selecione..."
        />
        <Input
          label="Chave Pix *"
          value={values.pixChave}
          onChange={(e) => onPatch({ pixChave: e.target.value })}
          error={errors.pixChave}
        />
        <Input
          label="Conta em nome *"
          value={values.pixContaEmNome}
          onChange={(e) => onPatch({ pixContaEmNome: e.target.value })}
          error={errors.pixContaEmNome}
          hint="Nome do titular da conta Pix"
        />
        <FileUpload
          anexoFile={values.anexoFile}
          existingAnexoName={existingAnexoName}
          onChange={(anexoFile) => onPatch({ anexoFile })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Input
        label="Titular da conta *"
        value={values.transferenciaTitular}
        onChange={(e) => onPatch({ transferenciaTitular: e.target.value })}
        error={errors.transferenciaTitular}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Banco *"
          value={values.transferenciaBanco}
          onChange={(e) => onPatch({ transferenciaBanco: e.target.value })}
          error={errors.transferenciaBanco}
        />
        <Input
          label="Agência *"
          value={values.transferenciaAgencia}
          onChange={(e) => onPatch({ transferenciaAgencia: e.target.value })}
          error={errors.transferenciaAgencia}
        />
      </div>
      <Input
        label="Conta *"
        value={values.transferenciaConta}
        onChange={(e) => onPatch({ transferenciaConta: e.target.value })}
        error={errors.transferenciaConta}
      />
      <FileUpload
        anexoFile={values.anexoFile}
        existingAnexoName={existingAnexoName}
        onChange={(anexoFile) => onPatch({ anexoFile })}
      />
    </div>
  );
}
